// src/pages/supervisor/Objectives.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import {
  Button,
  Card,
  Col,
  Row,
  Table,
  Badge,
  ProgressBar,
  ButtonGroup,
  Spinner,
  Container,
  Alert,
  Placeholder
} from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import axios from "axios";
import {
  Bullseye,
  Calendar,
  Repeat,
  ExclamationCircle,
  People,
  Cash,
  GraphUp,
  Award,
  Trophy,
  Person,
  Clock,
  CheckCircle,
  XCircle,
  ArrowUp, 
  TrendingDown
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../../constants/api_constants";

export default function Objectives() {
  const defaultCurrency = sessionStorage.getItem("default_currency") || 'XOF';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [timeRange, setTimeRange] = useState("month");
  const [selectedPeriod, setSelectedPeriod] = useState("monthly");
  const [sortField, setSortField] = useState("percentage");
  const [sortDirection, setSortDirection] = useState("desc");

  const getAuthToken = () => sessionStorage.getItem("authToken");
  const getHeaders = () => {
    const token = getAuthToken();
    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ' + defaultCurrency;
  };

  const loadData = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      const params = { time_range: timeRange };

      const response = await axios.get(`${API_BASE_URL}/supervisor/objectives`, {
        headers: getHeaders(),
        params
      });

      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError(response.data.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error("Erreur chargement objectifs:", err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadData();
  }, [timeRange]);

  const getPeriodColor = (percentage) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 50) return 'warning';
    return 'danger';
  };

  const getPeriodIcon = (percentage) => {
    if (percentage >= 80) return <CheckCircle className="text-success" size={18} />;
    if (percentage >= 50) return <Clock className="text-warning" size={18} />;
    return <XCircle className="text-danger" size={18} />;
  };

  const getPeriodStatus = (percentage) => {
    if (percentage >= 80) return { label: 'Atteint', color: 'success' };
    if (percentage >= 50) return { label: 'En cours', color: 'warning' };
    return { label: 'À améliorer', color: 'danger' };
  };

  // Configuration du graphique d'évolution
  const evolutionChartOptions = useMemo(() => ({
    chart: {
      type: 'area',
      height: 300,
      toolbar: { show: true, tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true } },
      background: 'transparent',
      animations: { enabled: true, easing: 'easeinout', speed: 800 }
    },
    colors: ['#3498db', '#2ecc71'],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 0.5,
        opacityFrom: 0.8,
        opacityTo: 0.1,
        stops: [0, 100]
      }
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 3 },
    xaxis: {
      categories: data?.evolution?.map(e => e.label) || [],
      labels: { style: { colors: '#6c757d', fontSize: '11px' } }
    },
    yaxis: {
      labels: {
        formatter: (value) => value.toLocaleString('fr-FR'),
        style: { colors: '#6c757d', fontSize: '11px' }
      }
    },
    tooltip: {
      y: { formatter: (value) => `${value.toLocaleString('fr-FR')} ${defaultCurrency}` },
      shared: true,
      intersect: false
    },
    legend: {
      position: 'top',
      horizontalAlign: 'center',
      fontSize: '12px',
      fontWeight: 600
    },
    grid: { borderColor: '#e0e6ed', strokeDashArray: 5 }
  }), [data, defaultCurrency]);

  const evolutionSeries = useMemo(() => [
    { name: 'Objectif', data: data?.evolution?.map(e => e.target) || [] },
    { name: 'Collecté', data: data?.evolution?.map(e => e.collected) || [] }
  ], [data]);

  // Skeleton components
  const SkeletonCard = () => (
    <Card className="border-0 shadow-sm h-100">
      <Card.Body className="p-3">
        <Placeholder as="div" animation="glow">
          <Placeholder xs={6} style={{ height: '14px' }} className="mb-2" />
          <Placeholder xs={8} style={{ height: '30px' }} className="mb-2" />
          <Placeholder xs={10} style={{ height: '6px' }} className="mb-2" />
          <Placeholder xs={4} style={{ height: '12px' }} />
        </Placeholder>
      </Card.Body>
    </Card>
  );

  const SkeletonChart = () => (
    <Card className="border-0 shadow-sm h-100">
      <Card.Body>
        <div className="bg-light rounded-3" style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spinner animation="border" variant="secondary" size="sm" />
          <span className="text-muted small ms-2">Chargement...</span>
        </div>
      </Card.Body>
    </Card>
  );

  const SkeletonTable = () => (
    <Card className="border-0 shadow-sm">
      <Card.Body className="p-0">
        <div className="table-responsive">
          <table className="table mb-0">
            <thead className="bg-light">
              <tr>
                {[...Array(6)].map((_, i) => (
                  <th key={i} className="py-2">
                    <Placeholder as="span" animation="glow" xs={i === 0 ? 2 : i === 1 ? 6 : i === 2 ? 4 : i === 3 ? 4 : i === 4 ? 4 : 4} style={{ height: '10px', display: 'block' }} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...Array(4)].map((_, i) => (
                <tr key={i}>
                  {[...Array(6)].map((_, j) => (
                    <td key={j} className="py-2">
                      <Placeholder as="span" animation="glow" xs={i === 0 ? 2 : i === 1 ? 6 : i === 2 ? 4 : i === 3 ? 4 : i === 4 ? 4 : i === 5 ? 4 : 4}  style={{ height: '8px', display: 'block' }} />
                    </td>
                  ))}
                </tr>
               ))}
            </tbody>
          </table>
        </div>
      </Card.Body>
    </Card>
  );

  // Objectif Card Component
  const ObjectiveCard = ({ period, data }) => {
    if (!data) return null;
    
    return (
      <Col md={6} lg={3}>
        <Card className="border-0 shadow-sm h-100 objective-card">
          <div className={`position-absolute top-0 start-0 w-100 bg-${getPeriodColor(data.percentage)}`} style={{ height: '4px' }} />
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <span className="text-muted small text-uppercase fw-semibold">{period}</span>
              {getPeriodIcon(data.percentage)}
            </div>
            <div className="d-flex align-items-center gap-2 mb-2">
              <h4 className="fw-bold mb-0">{data.percentage}%</h4>
              <Badge bg={getPeriodColor(data.percentage)} className="rounded-pill" style={{ fontSize: '9px' }}>
                {getPeriodStatus(data.percentage).label}
              </Badge>
            </div>
            <ProgressBar 
              now={Math.min(data.percentage, 100)} 
              variant={getPeriodColor(data.percentage)} 
              style={{ height: '6px' }} 
              className="mb-2"
            />
            <div className="d-flex justify-content-between small">
              <span className="text-muted">Collecté</span>
              <span className="text-success fw-bold">{formatCurrency(data.collected)}</span>
            </div>
            <div className="d-flex justify-content-between small">
              <span className="text-muted">Objectif</span>
              <span className="text-muted">{formatCurrency(data.target)}</span>
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <Container fluid>
          {/* En-tête */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
            <div>
              <div className="d-flex align-items-center gap-3">
                <div className="bg-primary bg-opacity-10 p-3 rounded-3">
                  <Bullseye size={32} className="text-primary" />
                </div>
                <div>
                  <h4 className="main-title mb-0 fw-bold" style={{ fontSize: '24px' }}>
                    Objectifs du Marché
                  </h4>
                  <p className="text-muted mb-0 d-flex align-items-center gap-2">
                    <Calendar size={14} />
                    Suivi des objectifs • {data?.marche_nom || 'Marché'}
                    <span className="text-muted">•</span>
                    {data?.period_label || ''}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="d-flex gap-2 flex-wrap mt-3 mt-md-0">
              <ButtonGroup size="sm" className="shadow-sm">
                <Button 
                  variant={timeRange === "week" ? "primary" : "outline-primary"} 
                  onClick={() => setTimeRange("week")}
                  className="px-3"
                  disabled={loading}
                >
                  Semaine
                </Button>
                <Button 
                  variant={timeRange === "month" ? "primary" : "outline-primary"} 
                  onClick={() => setTimeRange("month")}
                  className="px-3"
                  disabled={loading}
                >
                  Mois
                </Button>
                <Button 
                  variant={timeRange === "year" ? "primary" : "outline-primary"} 
                  onClick={() => setTimeRange("year")}
                  className="px-3"
                  disabled={loading}
                >
                  Année
                </Button>
              </ButtonGroup>

              <Button 
                variant="outline-secondary" 
                size="sm" 
                className="shadow-sm"
                onClick={() => loadData(true)} 
                disabled={loading}
              >
                {loading ? <Spinner animation="border" size="sm" /> : <Repeat size={16} />}
              </Button>
            </div>
          </div>

          {/* Erreur */}
          {error && (
            <Alert variant="danger" className="border-0 shadow-sm d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <ExclamationCircle size={24} className="me-2" />
                <span className="fw-medium">{error}</span>
              </div>
              <Button variant="outline-danger" size="sm" onClick={() => loadData(true)}>
                <Repeat className="me-1" size={14} /> Réessayer
              </Button>
            </Alert>
          )}

          {/* Cartes des objectifs */}
          <Row className="g-3 mb-4">
            {loading ? (
              <>
                <ObjectiveCard period="Journalier" data={null} loading />
                <ObjectiveCard period="Hebdomadaire" data={null} loading />
                <ObjectiveCard period="Mensuel" data={null} loading />
                <ObjectiveCard period="Annuel" data={null} loading />
              </>
            ) : (
              <>
                {data?.daily && <ObjectiveCard period="Journalier" data={data.daily} />}
                {data?.weekly && <ObjectiveCard period="Hebdomadaire" data={data.weekly} />}
                {data?.monthly && <ObjectiveCard period="Mensuel" data={data.monthly} />}
                {data?.yearly && <ObjectiveCard period="Annuel" data={data.yearly} />}
              </>
            )}
          </Row>

          {/* Évolution et Total */}
          <Row className="g-4 mb-4">
            <Col lg={8}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Header className="bg-white border-bottom py-2">
                  <Card.Title as="h6" className="mb-0 fw-semibold" style={{ fontSize: '13px' }}>
                    <GraphUp className="me-2 text-primary" size={16} />
                    Évolution des objectifs
                  </Card.Title>
                  <small className="text-muted">Suivi sur 7 jours</small>
                </Card.Header>
                <Card.Body>
                  {loading ? (
                    <SkeletonChart />
                  ) : data?.evolution && data.evolution.length > 0 ? (
                    <ReactApexChart 
                      options={evolutionChartOptions} 
                      series={evolutionSeries} 
                      type="area" 
                      height={300} 
                    />
                  ) : (
                    <div className="text-center py-4">
                      <ExclamationCircle size={48} className="text-muted opacity-25 mb-3" />
                      <p className="text-muted">Aucune donnée d'évolution</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Header className="bg-white border-bottom py-2">
                  <Card.Title as="h6" className="mb-0 fw-semibold" style={{ fontSize: '13px' }}>
                    <Award className="me-2 text-primary" size={16} />
                    Objectif Global
                  </Card.Title>
                  <small className="text-muted">Vue d'ensemble</small>
                </Card.Header>
                <Card.Body className="d-flex flex-column justify-content-center">
                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" variant="secondary" />
                    </div>
                  ) : data?.total ? (
                    <>
                      <div className="text-center mb-3">
                        <div className="display-4 fw-bold text-primary">{data.total.percentage}%</div>
                        <Badge bg={getPeriodColor(data.total.percentage)} className="rounded-pill px-3 py-2">
                          {getPeriodStatus(data.total.percentage).label}
                        </Badge>
                      </div>
                      <ProgressBar 
                        now={Math.min(data.total.percentage, 100)} 
                        variant={getPeriodColor(data.total.percentage)} 
                        style={{ height: '8px' }} 
                        className="mb-3"
                      />
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">Collecté</span>
                        <span className="text-success fw-bold">{formatCurrency(data.total.collected)}</span>
                      </div>
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">Objectif</span>
                        <span className="text-muted">{formatCurrency(data.total.target)}</span>
                      </div>
                      <div className="d-flex justify-content-between small mt-2 pt-2 border-top">
                        <span className="text-muted">Écart</span>
                        <span className={data.total.collected >= data.total.target ? 'text-success' : 'text-danger'}>
                          {formatCurrency(Math.abs(data.total.target - data.total.collected))}
                          {data.total.collected >= data.total.target ? ' ✅' : ' ⚠️'}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-muted">Aucune donnée disponible</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Objectifs par agent */}
          <Row>
            <Col lg={12}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-2">
                  <div>
                    <Card.Title as="h6" className="mb-0 fw-semibold" style={{ fontSize: '13px' }}>
                      <Person className="me-2 text-primary" size={16} />
                      Objectifs par Agent
                    </Card.Title>
                    <small className="text-muted">Performance individuelle</small>
                  </div>
                  <Badge bg="primary" className="rounded-pill">
                    {data?.agents_objectives?.length || 0} agents
                  </Badge>
                </Card.Header>
                <Card.Body className="p-0">
                  {loading ? (
                    <SkeletonTable />
                  ) : data?.agents_objectives && data.agents_objectives.length > 0 ? (
                    <Table hover responsive className="mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="py-2">#</th>
                          <th className="py-2">Agent</th>
                          <th className="py-2 text-end">Rôle</th>
                          <th className="py-2 text-end">Contribuables</th>
                          <th className="py-2 text-end">Objectif</th>
                          <th className="py-2 text-end">Collecté</th>
                          <th className="py-2 text-center">Performance</th>
                          <th className="py-2 text-center">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.agents_objectives.map((agent, index) => (
                          <tr key={agent.agent_id} className="align-middle">
                            <td>
                              {index === 0 ? <Trophy className="text-warning" size={16} /> : index + 1}
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center me-2" style={{ width: '28px', height: '28px' }}>
                                  <Person size={12} className="text-primary" />
                                </div>
                                <span className="fw-medium" style={{ fontSize: '13px' }}>{agent.nom}</span>
                              </div>
                            </td>
                            <td className="text-end">
                              <Badge bg="secondary" className="rounded-pill" style={{ fontSize: '9px' }}>
                                {agent.role || 'Agent'}
                              </Badge>
                            </td>
                            <td className="text-end">{agent.nb_contribuables}</td>
                            <td className="text-end text-muted">{formatCurrency(agent.objectif)}</td>
                            <td className="text-end text-success">{formatCurrency(agent.collecte)}</td>
                            <td className="text-center">
                              <div className="d-flex align-items-center justify-content-center gap-2">
                                <ProgressBar 
                                  now={Math.min(agent.percentage, 100)} 
                                  variant={getPeriodColor(agent.percentage)} 
                                  style={{ width: '80px', height: '4px' }} 
                                />
                                <span className={`fw-bold text-${getPeriodColor(agent.percentage)}`} style={{ fontSize: '12px' }}>
                                  {agent.percentage}%
                                </span>
                              </div>
                            </td>
                            <td className="text-center">
                              <Badge bg={getPeriodColor(agent.percentage)} className="rounded-pill" style={{ fontSize: '9px' }}>
                                {getPeriodStatus(agent.percentage).label}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-center py-4">
                      <ExclamationCircle size={48} className="text-muted opacity-25 mb-3" />
                      <p className="text-muted">Aucun agent trouvé</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Footer />
        </Container>
      </div>

      <style>{`
        .objective-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          border-radius: 12px;
          overflow: hidden;
        }
        .objective-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.08) !important;
        }
        .main-title {
          font-weight: 700;
        }
        .card {
          border-radius: 12px !important;
        }
        .shadow-sm {
          box-shadow: 0 2px 8px rgba(0,0,0,0.06) !important;
        }
        .table > :not(caption) > * > * {
          padding: 8px 12px;
        }
        .btn-group .btn {
          border-radius: 6px !important;
        }
        .btn-group .btn:not(:first-child) {
          border-top-left-radius: 0 !important;
          border-bottom-left-radius: 0 !important;
        }
        .btn-group .btn:not(:last-child) {
          border-top-right-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
        }
      `}</style>
    </React.Fragment>
  );
}